/**
 * Utility to create an HTMLImageElement from a given URL.
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Prevent CORS issues
    image.onload = () => resolve(image);
    image.onerror = (err) => reject(new Error(`Failed to load image: ${url}\n${err}`));
    image.src = url;
  });

/**
 * Crops an image using pixel dimensions and returns a File object
 * with a dynamic, nice file name.
 * @param {string} imageSrc - Source URL of the image
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop - Cropping area
 * @param {string} [namePrefix='profile'] - Optional prefix for the file name
 * @returns {Promise<File>} - Cropped image file
 */
export default async function getCroppedImg(imageSrc, pixelCrop, namePrefix = 'profile') {
  if (!imageSrc || !pixelCrop) {
    throw new Error('Missing required parameters: imageSrc or pixelCrop.');
  }

  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty or failed to create blob.'));
        return;
      }
      const file = new File([blob], `${namePrefix}_.jpeg`, { type: 'image/jpeg' });
      resolve(file);
    }, 'image/jpeg', 0.95); // 0.95 = high quality
  });
}
